import { Fragment, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, ChevronRight, Archive, ArchiveRestore, Wallet } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PageHeader } from '@/components/ui/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { TableSkeleton } from '@/components/ui/skeleton'
import { useBalances, useDeleteAccount, useArchiveAccount, fetchAccountTxCounts } from '@/hooks/useAccounts'
import { useConfirm } from '@/context/ConfirmProvider'
import { useT } from '@/i18n/I18nProvider'
import { formatCurrency } from '@/lib/currency'
import { accountIcon } from '@/lib/accountIcon'
import { AccountFormDialog } from '@/components/accounts/AccountFormDialog'
import type { Account } from '@/types/db'

// Ordem fixa dos tipos na aba Contas.
const TYPE_ORDER: Account['type'][] = ['corrente', 'poupanca', 'cash', 'carteira', 'investimento', 'cartao']

export function AccountsPage() {
  const { t } = useT()
  const navigate = useNavigate()
  const [showArchived, setShowArchived] = useState(false)
  const { data: balances, isLoading } = useBalances(showArchived)
  const del = useDeleteAccount()
  const archive = useArchiveAccount()
  const confirm = useConfirm()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Account | null>(null)

  async function unarchive(a: Account) {
    try {
      await archive.mutateAsync({ id: a.id, archived: false })
      toast.success(t('acct.unarchived'))
    } catch (e) {
      toast.error(t('acct.unarchiveError'), { description: (e as Error).message })
    }
  }

  // Agrupa por TIPO; dentro de cada tipo, maior saldo no topo. Cartões ficam
  // na aba Cartões (aqui só contas).
  const groups = useMemo(() => {
    const m = new Map<Account['type'], { account: Account; balance: number }[]>()
    for (const b of balances ?? []) {
      if (b.account.type === 'cartao') continue
      if (!m.has(b.account.type)) m.set(b.account.type, [])
      m.get(b.account.type)!.push(b)
    }
    for (const arr of m.values())
      arr.sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance) || a.account.name.localeCompare(b.account.name))
    return TYPE_ORDER.filter((t) => m.has(t)).map((t) => ({ type: t, items: m.get(t)! }))
  }, [balances])

  function openNew() {
    setEditing(null)
    setDialogOpen(true)
  }
  function openEdit(a: Account) {
    setEditing(a)
    setDialogOpen(true)
  }
  async function remove(a: Account) {
    let counts: { paid: number; pending: number }
    try {
      counts = await fetchAccountTxCounts(a.id)
    } catch (e) {
      toast.error(t('acct.checkError'), { description: (e as Error).message })
      return
    }
    if (counts.paid > 0) {
      const ok = await confirm({
        title: t('acct.archiveConfirmTitle', { name: a.name }),
        description: t('acct.archiveConfirmDesc', { count: counts.paid }),
        confirmText: t('acct.archiveAction'),
        tone: 'warning',
        icon: 'archive',
      })
      if (!ok) return
      try {
        await archive.mutateAsync({ id: a.id })
        toast.success(t('acct.archived'))
      } catch (e) {
        toast.error(t('acct.archiveError'), { description: (e as Error).message })
      }
      return
    }
    const ok = await confirm({
      title: t('acct.deleteConfirmTitle', { name: a.name }),
      description:
        counts.pending > 0
          ? t('acct.deleteConfirmDescPending', { count: counts.pending })
          : t('acct.cannotUndo'),
      confirmText: t('common.delete'),
      tone: 'destructive',
    })
    if (!ok) return
    try {
      await del.mutateAsync(a.id)
      toast.success(t('acct.deleted'))
    } catch (e) {
      toast.error(t('acct.deleteError'), { description: (e as Error).message })
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={t('nav.accounts')}
        subtitle={t('acct.accountsSubtitle')}
        actions={
          <>
            <Button variant="outline" onClick={() => setShowArchived((v) => !v)}>
              <Archive className="size-4" />
              {showArchived ? t('acct.hideArchived') : t('acct.showArchived')}
            </Button>
            <Button onClick={openNew}>
              <Plus className="size-4" /> {t('acct.newAccount')}
            </Button>
          </>
        }
      />

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <TableSkeleton rows={6} cols={4} />
          ) : groups.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('acct.colAccount')}</TableHead>
                    <TableHead>{t('settings.currency')}</TableHead>
                    <TableHead className="text-right">{t('acct.colBalance')}</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((g) => (
                    <Fragment key={g.type}>
                      <TableRow className="bg-muted/40 hover:bg-muted/40">
                        <TableCell colSpan={4} className="py-1.5 text-xs font-medium">
                          {t(`acct.type.${g.type}`)} <span className="text-muted-foreground">({g.items.length})</span>
                        </TableCell>
                      </TableRow>
                      {g.items.map(({ account, balance }) => (
                        <TableRow
                          key={account.id}
                          className={`hover:bg-accent/40 cursor-pointer ${account.archived ? 'opacity-60' : ''}`}
                          onClick={() => navigate(`/contas/${account.id}`)}
                        >
                          <TableCell className="font-medium">
                            <span className="inline-flex items-center gap-2">
                              <span className="text-base leading-none">{accountIcon(account)}</span>
                              {account.name}
                              {account.archived && (
                                <Badge variant="outline" className="text-muted-foreground text-xs">
                                  {t('acct.archivedBadge')}
                                </Badge>
                              )}
                            </span>
                          </TableCell>
                          <TableCell>{account.currency}</TableCell>
                          <TableCell
                            className={`text-right tabular-nums whitespace-nowrap ${balance < -0.005 ? 'text-destructive' : ''}`}
                          >
                            {formatCurrency(balance, account.currency)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              {account.archived ? (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    unarchive(account)
                                  }}
                                  aria-label={t('acct.unarchive')}
                                  title={t('acct.unarchive')}
                                >
                                  <ArchiveRestore className="size-4" />
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      openEdit(account)
                                    }}
                                    aria-label={t('common.edit')}
                                  >
                                    <Pencil className="size-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      remove(account)
                                    }}
                                    aria-label={t('common.delete')}
                                  >
                                    <Trash2 className="text-destructive size-4" />
                                  </Button>
                                </>
                              )}
                              <ChevronRight className="text-muted-foreground size-4" />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState
              icon={Wallet}
              title={t('acct.emptyTitle')}
              description={t('acct.emptyDesc')}
              action={
                <Button onClick={openNew}>
                  <Plus className="size-4" /> {t('acct.newAccount')}
                </Button>
              }
            />
          )}
        </CardContent>
      </Card>

      <AccountFormDialog open={dialogOpen} onOpenChange={setDialogOpen} account={editing} />
    </div>
  )
}
